/** "Partilhar sessão" e "abrir uma sessão partilhada": as acções.
 *
 *  Partilhar (quem tem a sessão aberta):
 *   1. os modelos abertos que ainda não estão no servidor sobem pelo
 *      caminho dos modelos partilhados (em blocos, com os limites e a
 *      quota do workspace), só para quem os envia;
 *   2. a triagem dos conflitos fica partilhada (a que já está, ou uma
 *      nova com o código que a janela pediu);
 *   3. cada BCF carregado fica sincronizado (registado e enviado uma vez);
 *   4. grava-se a sessão no servidor: o manifesto, as peças e a escolha
 *      do workspace inteiro. A janela dá depois acesso às pessoas, e o
 *      servidor propaga essa lista às peças.
 *
 *  Abrir (quem recebe): lê a sessão, descarrega os modelos para a cache
 *  do browser (pelo SHA-256: o que já cá está não volta a descer), e
 *  repõe-na como se fosse um ficheiro de sessão (modelos, pontos de
 *  vista, vistas 2D, disposição), entra na triagem partilhada e liga os
 *  BCF ao vivo.
 */
import { ApiError } from '@core/api/client'
import { useAppStore } from '@core/store'
import { abrirJanelaPartilhar, type OpcoesDaCriacao } from '@core/partilhas/partilhasEstado'
import { abrirBcfDaPartilha, abrirTriagemDaPartilha } from '@core/partilhas/abrirPartilhado'
import { partilhasApi } from '@core/partilhas/partilhasApi'
import { resumoDasPecas, visivelPara } from '@core/partilhas/partilhasRegras'
import {
  garantirModeloNoServidor,
  modelosPartilhadosAgora,
  recarregarModelos,
} from '@core/modelos/modelosPartilhados'
import {
  comprimirGzip,
  descarregarModelo,
  descomprimirGzip,
  modelosApi,
  sha256Hex,
} from '@core/modelos/modelosApi'
import { formatarBytes } from '@core/modelos/modelosRegras'
import { ifcPersistence } from '@core/session/ifcPersistence'
import { closeSession } from '@core/session/closeSession'
import {
  applyLocalWorkSnapshot,
  collectLocalWorkSnapshot,
  getRecoveryStatus,
  markWorkSaved,
  whenRecoveryResolved,
} from '@core/session/recovery'
import { blobToDataUrl, dataUrlToBlob, type VnSerialViewpoint } from '@core/session/session'
import type { WorkspaceLayoutMode } from '@core/store/workspaceLayoutSlice'
import { placementsDiffer } from '@viewer/lib/geoTransform'
import { viewpointsStore } from '@viewer/lib/viewpointsStore'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  partilharTriagemParaSessao,
  triagemDaSessao,
} from '@viewer/clash/clashPartilhaControlo'
import { verTriagem } from '@viewer/clash/clashPartilhaPorta'
import { projetoDoBcf } from '@modules/bcf/lib/bcfProjeto'
import { bcfSyncApi, lerBaseGuardada, portaDoBcf } from '@modules/bcf/lib/bcfSyncPorta'
import { sincronizar } from '@modules/bcf/lib/bcfSincronizar'
import { sessoesApi } from './sessoesApi'
import {
  BLOCO_DA_SESSAO,
  blocosDoPacote,
  chaveNova,
  construirManifesto,
  lerManifesto,
  lerPacote,
  modelosParaReposicao,
  nomeSugerido,
  pacoteCabe,
  pecaDaPartilha,
  pecasDoManifesto,
  pecasPorConfirmar,
  planoDeAbertura,
  semMiniaturas,
  textoDeSessaoAdiada,
  textoDeSubstituir,
  textoDoResultado,
  type BcfDaSessao,
  type ManifestoDaSessao,
  type ModeloDaSessao,
  type PacoteDosPontosDeVista,
  type PecaDaSessao,
  type TriagemDaSessao,
} from './sessoesRegras'
import {
  definirProgresso,
  esquecerSessaoActual,
  gravarSessaoActual,
  lerSessaoActual,
  sessoesMudaram,
} from './sessaoPartilhadaEstado'

export interface ContextoDeSessao {
  workspaceId: string
  workspaceSlug: string
  workspaceNome: string
  utilizadorId: string
  utilizadorNome: string
}

function mensagem(err: unknown): string {
  return err instanceof Error ? err.message : 'Não foi possível falar com o servidor.'
}

// ---------------------------------------------------------------- partilhar

/** Abre a janela "Partilhar com..." para a sessão: por partilhar (com o
 *  nome e, se for preciso, o código da triagem), ou já partilhada (gerir
 *  a lista, actualizar, retirar). */
export async function abrirPartilharSessao(ctx: ContextoDeSessao): Promise<void> {
  const actual = lerSessaoActual(ctx.workspaceId)
  if (actual) {
    abrirJanelaPartilhar({
      tipo: 'sessao',
      nome: actual.nome,
      partilhaId: actual.partilhaId,
      workspaceNome: ctx.workspaceNome,
      // A lista da sessão propaga às peças: as linhas dos modelos
      // ("Visível para: ...") têm de se actualizar também.
      aoMudar: () => void recarregarModelos(),
      atualizar:
        actual.minha && actual.chave
          ? async (opcoes) => {
              await prepararEPartilhar(ctx, actual.chave ?? chaveNova(), actual.nome, false, opcoes)
            }
          : undefined,
    })
    return
  }
  const st = useAppStore.getState()
  let triagem: Awaited<ReturnType<typeof triagemDaSessao>> = { activa: null, pareceres: 0 }
  try {
    triagem = await triagemDaSessao(ctx.workspaceId)
  } catch {
    /* sem a triagem: a sessão vai sem ela */
  }
  const precisaCodigo = !triagem.activa && triagem.pareceres > 0
  const nome = nomeSugerido({
    triagem: triagem.activa?.codigo ?? null,
    modelos: st.models.map((m) => m.fileName),
  })
  const partes: string[] = []
  if (st.models.length) partes.push(`${st.models.length} modelo${st.models.length === 1 ? '' : 's'}`)
  if (triagem.activa) partes.push(`a triagem "${triagem.activa.codigo}"`)
  else if (triagem.pareceres) partes.push(`a triagem dos conflitos (${triagem.pareceres} parecer${triagem.pareceres === 1 ? '' : 'es'})`)
  if (st.bcfLoaded.length) partes.push(`${st.bcfLoaded.length} BCF`)
  // Uma chave por janela: carregar outra vez em "Partilhar" depois de
  // uma falha a meio actualiza a mesma sessão em vez de criar outra.
  const chave = chaveNova()
  abrirJanelaPartilhar({
    tipo: 'sessao',
    nome,
    partilhaId: null,
    workspaceNome: ctx.workspaceNome,
    aoMudar: () => void recarregarModelos(),
    campos: { nome, ...(precisaCodigo ? { codigoTriagem: '' } : {}) },
    descricao: partes.length
      ? `Vai partilhar ${partes.length > 1 ? `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}` : partes[0]}, com os pontos de vista e as vistas 2D.`
      : 'A sessão está vazia: abra modelos (ou um BCF) antes de a partilhar.',
    criar: (workspaceInteiro, opcoes) =>
      prepararEPartilhar(ctx, chave, (opcoes.nome ?? '').trim() || nome, workspaceInteiro, opcoes),
  })
}

async function pontosDeVista(): Promise<VnSerialViewpoint[]> {
  try {
    const lista = await viewpointsStore.list()
    return await Promise.all(
      lista.map(async (vp) => {
        const { thumbnail, ...rest } = vp
        let dataUrl: string | null = null
        let mime: string | null = null
        if (thumbnail) {
          try {
            dataUrl = await blobToDataUrl(thumbnail)
            mime = thumbnail.type || null
          } catch {
            /* sem miniatura */
          }
        }
        return { record: rest, thumbnail_data_url: dataUrl, thumbnail_mime: mime }
      }),
    )
  } catch {
    return []
  }
}

interface Pacote {
  dados: Blob
  info: PacoteDosPontosDeVista
}

async function embrulhar(lista: readonly VnSerialViewpoint[], sem: boolean): Promise<Pacote> {
  const dados = await comprimirGzip(new Blob([JSON.stringify(lista)], { type: 'application/json' }))
  return {
    dados,
    info: {
      blocos: blocosDoPacote(dados.size),
      bytes: dados.size,
      sha256: await sha256Hex(await dados.arrayBuffer()),
      quantos: lista.length,
      sem_miniaturas: sem,
    },
  }
}

/** O pacote dos pontos de vista (o JSON comprimido): com as miniaturas,
 *  ou sem elas se assim não couber. `null` sem pontos de vista. */
async function pacoteDosPontosDeVista(lista: readonly VnSerialViewpoint[]): Promise<Pacote | null> {
  if (lista.length === 0) return null
  const completo = await embrulhar(lista, false)
  if (pacoteCabe(completo.info.bytes)) return completo
  const leve = await embrulhar(semMiniaturas(lista), true)
  if (pacoteCabe(leve.info.bytes)) return leve
  throw new Error(
    'Os pontos de vista da sessão não cabem no servidor, nem sem as miniaturas. Apague alguns e partilhe de novo.',
  )
}

/** Envia o que falta, partilha as peças e grava a sessão. Devolve o id
 *  da partilha da sessão. Um erro (sem espaço, modelo grande de mais,
 *  código da triagem em falta...) sobe para a janela o mostrar; o que já
 *  subiu fica no servidor, só para quem o enviou. */
export async function prepararEPartilhar(
  ctx: ContextoDeSessao,
  chave: string,
  nome: string,
  workspaceInteiro: boolean,
  opcoes: OpcoesDaCriacao,
): Promise<string> {
  const { progresso, aviso } = opcoes
  const st = useAppStore.getState()
  if (st.models.length === 0 && st.bcfLoaded.length === 0) {
    throw new Error('A sessão está vazia: abra modelos (ou um BCF) antes de a partilhar.')
  }
  try {
    // 1. Os modelos.
    progresso('A ver que modelos já estão no servidor…')
    await recarregarModelos()
    if (modelosPartilhadosAgora().estado?.ativo === false) {
      throw new Error('Os modelos partilhados estão desligados neste workspace: não há onde pôr os modelos.')
    }
    const modelos: ModeloDaSessao[] = []
    const abertos = st.models
    for (let i = 0; i < abertos.length; i++) {
      const m = abertos[i]
      if (m.sourceFormat === 'frag' || m.rawIfcAvailable === false) {
        aviso(`"${m.fileName}" foi aberto em Fragments e não vai na sessão (só se partilham ficheiros IFC).`)
        continue
      }
      const rotulo = `"${m.fileName}" (${i + 1} de ${abertos.length})`
      progresso(`A preparar ${rotulo}…`)
      const modelo = await garantirModeloNoServidor(m, (feito, total) =>
        progresso(`A enviar ${rotulo}: bloco ${feito} de ${total}…`),
      )
      const override = st.geoOverrides[m.id]
      const baseline = st.geoBaselines[m.id]
      modelos.push({
        nome: m.fileName,
        sha256: m.sha256.toLowerCase(),
        schema_version: m.schemaVersion,
        placement_override:
          override && (!baseline || placementsDiffer(override, baseline)) ? override : null,
        partilha_id: modelo.partilha_id ?? null,
      })
      if (!modelo.partilha_id) aviso(`"${m.fileName}" está no servidor, mas sem partilha: ficou de fora.`)
    }

    // 2. A triagem dos conflitos.
    let triagem: TriagemDaSessao | null = null
    progresso('A preparar a triagem dos conflitos…')
    const t = await triagemDaSessao(ctx.workspaceId).catch(() => ({ activa: null, pareceres: 0 }))
    if (t.activa) {
      try {
        const p = await verTriagem(t.activa.projetoId)
        triagem = { projeto_id: p.id, codigo: p.codigo, partilha_id: p.partilha_id ?? null }
      } catch (err) {
        aviso(`A triagem "${t.activa.codigo}" não foi incluída: ${mensagem(err)}`)
      }
    } else if (t.pareceres > 0) {
      const codigo = (opcoes.codigoTriagem ?? '').trim()
      if (!codigo) {
        throw new Error('Escreva o código da triagem dos conflitos (por exemplo, Cha139) para a partilhar com a sessão.')
      }
      progresso(`A partilhar a triagem "${codigo}"…`)
      const p = await partilharTriagemParaSessao(
        { workspaceId: ctx.workspaceId, utilizadorId: ctx.utilizadorId, nome: ctx.utilizadorNome },
        codigo,
      )
      triagem = { projeto_id: p.id, codigo: p.codigo, partilha_id: p.partilha_id ?? null }
    }

    // 3. Os BCF.
    const bcfs: BcfDaSessao[] = []
    let bcfAtivo: string | null = null
    for (const b of st.bcfLoaded) {
      progresso(`A sincronizar o BCF "${b.name}"…`)
      try {
        let partilhaId: string
        if (b.partilhaId) {
          partilhaId = b.partilhaId
          if (!bcfs.some((x) => x.partilha_id === b.partilhaId)) {
            bcfs.push({ partilha_id: b.partilhaId, project_id: b.sourceProjectId ?? null, nome: b.name })
          }
        } else {
          const projeto = projetoDoBcf(b)
          const p =
            (await bcfSyncApi.partilha(projeto.id)) ??
            (await bcfSyncApi.registarPartilha(projeto.id, projeto.nome, false))
          // Nunca sincronizado aqui: envia-se uma vez, para quem receber
          // a sessão ter os tópicos. Depois é o ao vivo do painel.
          if (!(await lerBaseGuardada(ctx.workspaceSlug, projeto.id))) {
            const store = useAppStore.getState()
            await sincronizar(
              portaDoBcf(
                ctx.workspaceSlug,
                projeto.id,
                b.localId,
                () => useAppStore.getState().bcfTopicosParaSync(b.localId),
                (topicos, apagados) => store.bcfAplicarDaSync(b.localId, topicos, apagados),
              ),
            )
          }
          // Dois BCF com o mesmo projecto (o completo e o das críticas,
          // exportados da mesma triagem) são a mesma partilha: vai uma vez.
          partilhaId = p.id
          if (!bcfs.some((x) => x.partilha_id === p.id)) {
            bcfs.push({ partilha_id: p.id, project_id: projeto.partilhavel ? projeto.id : null, nome: b.name })
          }
        }
        if (b.localId === st.bcfActiveId) bcfAtivo = partilhaId
      } catch (err) {
        aviso(`O BCF "${b.name}" não foi incluído: ${mensagem(err)}`)
      }
    }

    // 4. A sessão.
    progresso('A preparar os pontos de vista…')
    const pacote = await pacoteDosPontosDeVista(await pontosDeVista())
    if (pacote?.info.sem_miniaturas) {
      aviso('Os pontos de vista seguem sem as miniaturas, para caberem no servidor.')
    }
    progresso('A gravar a sessão…')
    const ativo = st.models.find((m) => m.id === st.activeModelId)
    const manifesto = construirManifesto({
      nome,
      layout_mode: st.workspaceLayoutMode ?? null,
      modelos,
      modelo_ativo_sha256: ativo?.sha256.toLowerCase() ?? null,
      triagem,
      bcfs,
      bcf_ativo_partilha_id: bcfAtivo,
      pontos_de_vista: pacote?.info ?? null,
      local_work: collectLocalWorkSnapshot(),
    })
    const s = await sessoesApi.partilhar({
      chave,
      nome,
      manifesto,
      pecas: pecasDoManifesto(manifesto),
      workspace_inteiro: workspaceInteiro,
    })
    for (const a of s.avisos) aviso(a)
    gravarSessaoActual(ctx.workspaceId, { partilhaId: s.partilha.id, chave, nome, minha: true })
    sessoesMudaram()
    // Os pontos de vista, em blocos, depois de a sessão existir (é ela
    // que diz quantos são). Quem abrir a meio confirma pelo SHA-256.
    if (pacote) {
      for (let i = 0; i < pacote.info.blocos; i++) {
        progresso(`A enviar os pontos de vista: bloco ${i + 1} de ${pacote.info.blocos}…`)
        await sessoesApi.enviarBloco(
          s.partilha.id,
          i,
          pacote.dados.slice(i * BLOCO_DA_SESSAO, (i + 1) * BLOCO_DA_SESSAO),
        )
      }
    }
    return s.partilha.id
  } finally {
    progresso(null)
  }
}

// ------------------------------------------------------------------- abrir

async function esperarPeloViewer(ms = 20_000): Promise<boolean> {
  const fim = Date.now() + ms
  while (!viewerApi.hasIfcLoader()) {
    if (Date.now() > fim) return false
    await new Promise((r) => setTimeout(r, 200))
  }
  return true
}

/** Os pontos de vista da sessão: do pacote em blocos (confirmado pelo
 *  SHA-256), ou do próprio manifesto nas sessões que os levavam lá. */
async function pontosDeVistaDaSessao(
  id: string,
  manifesto: ManifestoDaSessao,
  avisos: string[],
): Promise<VnSerialViewpoint[]> {
  const p = manifesto.pontos_de_vista
  if (!p) return manifesto.viewpoints
  try {
    const partes: ArrayBuffer[] = []
    for (let i = 0; i < p.blocos; i++) {
      definirProgresso(`A descarregar os pontos de vista: bloco ${i + 1} de ${p.blocos}…`)
      partes.push(await sessoesApi.lerBloco(id, i))
    }
    const gz = new Blob(partes)
    if (gz.size !== p.bytes || (await sha256Hex(await gz.arrayBuffer())) !== p.sha256) {
      avisos.push(
        'Os pontos de vista da sessão ainda não estão inteiros no servidor (quem partilhou pode estar a '
          + 'enviá-los). Abra a sessão outra vez daqui a pouco.',
      )
      return []
    }
    const lista = lerPacote(JSON.parse(new TextDecoder().decode(await descomprimirGzip(gz))))
    if (p.sem_miniaturas) avisos.push('Os pontos de vista desta sessão vieram sem miniaturas, para caberem no servidor.')
    return lista
  } catch (err) {
    avisos.push(`Os pontos de vista da sessão não foram abertos: ${mensagem(err)}`)
    return []
  }
}

async function reporPontosDeVista(lista: readonly VnSerialViewpoint[]): Promise<void> {
  try {
    await viewpointsStore.clearAll()
    for (const vp of lista) {
      let blob: Blob | null = null
      if (vp.thumbnail_data_url) {
        const decoded = dataUrlToBlob(vp.thumbnail_data_url)
        blob =
          decoded && vp.thumbnail_mime
            ? new Blob([await decoded.arrayBuffer()], { type: vp.thumbnail_mime })
            : decoded
      }
      await viewpointsStore.save({ ...vp.record, thumbnail: blob })
    }
    const refresh = useAppStore.getState().refreshViewpoints
    if (typeof refresh === 'function') void refresh()
  } catch (err) {
    console.warn('[sessao-partilhada] pontos de vista não repostos', err)
  }
}

/**
 * Abre uma sessão partilhada: a sessão actual fecha (pergunta antes, se
 * houver alguma coisa aberta), os modelos descem para a cache do
 * browser e repõem-se como numa sessão gravada, a triagem e os BCF
 * ligam-se ao vivo. Devolve false se não abriu (cancelado, sem acesso).
 */
export async function abrirSessaoPartilhada(
  id: string,
  ctx: ContextoDeSessao,
  irParaOViewer: () => void,
): Promise<boolean> {
  const toast = useAppStore.getState().pushToast
  definirProgresso('A ler a sessão partilhada…')
  try {
    let s
    try {
      s = await sessoesApi.ver(id)
    } catch (err) {
      toast(
        err instanceof ApiError && err.status === 404
          ? 'Esta sessão não existe ou já não está partilhada consigo.'
          : `Não foi possível abrir a sessão: ${mensagem(err)}`,
        { type: 'error', timeout: 0 },
      )
      return false
    }
    const manifesto = lerManifesto(s.manifesto)
    const nome = s.partilha.nome ?? manifesto?.nome ?? 'Sessão'
    if (!manifesto) {
      toast(`A sessão "${nome}" foi gravada por uma versão mais recente da plataforma. Recarregue a página.`, {
        type: 'error',
        timeout: 0,
      })
      return false
    }
    // Trabalho da sessão anterior por recuperar: a sessão espera pela
    // resposta ao "Recuperar a sessão anterior?". Antes abria-se por
    // baixo da pergunta: a triagem ficava trancada (espera por essa
    // decisão) e "Recuperar" punha o trabalho antigo por cima da sessão;
    // "Descartar" depois de descarregar apagava o que tinha descido.
    if (getRecoveryStatus().phase !== 'ready') {
      definirProgresso('À espera da decisão sobre o trabalho anterior (Recuperar ou Descartar)…')
      if (await whenRecoveryResolved()) {
        toast(textoDeSessaoAdiada(nome), { type: 'info', timeout: 0 })
        return false
      }
      definirProgresso('A ler a sessão partilhada…')
    }
    const st = useAppStore.getState()
    const pergunta = textoDeSubstituir(nome, st.models.length, st.bcfLoaded.length)
    if (pergunta && !window.confirm(pergunta)) return false
    if (pergunta) await closeSession()
    esquecerSessaoActual()
    irParaOViewer()

    // O que não veio com a sessão mas quem abre pode ter por si.
    const porSi: PecaDaSessao[] = []
    for (const id of pecasPorConfirmar(manifesto, s.pecas)) {
      try {
        const peca = pecaDaPartilha(await partilhasApi.ver(id))
        if (peca) porSi.push(peca)
      } catch {
        /* sem acesso: o plano diz que ficou de fora */
      }
    }
    const plano = planoDeAbertura(manifesto, [...s.pecas.filter((p) => p.acesso), ...porSi])
    const avisos = [...plano.avisos]

    // Os modelos: para a cache do browser, pelo SHA-256.
    for (let i = 0; i < plano.modelos.length; i++) {
      const { modelo, peca } = plano.modelos[i]
      const rotulo = `"${modelo.nome}" (${i + 1} de ${plano.modelos.length})`
      try {
        if (await ifcPersistence.load(modelo.sha256)) continue
        definirProgresso(`A procurar ${rotulo}…`)
        const revisoes = await modelosApi.revisoesDaPartilha(peca.id)
        const rev =
          revisoes.find((r) => r.sha256.toLowerCase() === modelo.sha256) ??
          revisoes.find((r) => r.ultima) ??
          revisoes[0]
        if (!rev) {
          avisos.push(`O modelo "${modelo.nome}" já não tem revisões no servidor.`)
          continue
        }
        if (rev.sha256.toLowerCase() !== modelo.sha256) {
          avisos.push(
            `A revisão de "${modelo.nome}" que estava na sessão já não existe: abriu-se a revisão ${rev.revisao}.`,
          )
          modelo.sha256 = rev.sha256.toLowerCase()
        }
        definirProgresso(`A descarregar ${rotulo} (${formatarBytes(rev.tamanho_comprimido)})…`)
        const { bytes } = await descarregarModelo(rev.id, (feito, total) =>
          definirProgresso(`A descarregar ${rotulo}: bloco ${feito} de ${total}…`),
        )
        const guardado = await ifcPersistence.save({ sha256: rev.sha256, filename: rev.nome, bytes })
        if (!guardado) {
          avisos.push(
            `"${modelo.nome}" é grande de mais para a cache do browser: abra-o em "Partilhados comigo" ou em "Modelos do workspace".`,
          )
        }
      } catch (err) {
        avisos.push(`Não foi possível descarregar "${modelo.nome}": ${mensagem(err)}`)
      }
    }

    // A vista: pontos de vista, vistas 2D e disposição, antes dos modelos.
    const pontos = await pontosDeVistaDaSessao(s.partilha.id, manifesto, avisos)
    definirProgresso('A repor a vista…')
    await reporPontosDeVista(pontos)
    try {
      applyLocalWorkSnapshot(manifesto.local_work)
    } catch {
      /* as vistas 2D ficam como estavam */
    }
    if (manifesto.layout_mode) {
      try {
        useAppStore.getState().setWorkspaceLayoutMode(manifesto.layout_mode as WorkspaceLayoutMode)
      } catch {
        /* disposição desconhecida nesta versão */
      }
    }
    // O banner da sessão abre-os da cache (e diz o que faltar).
    await esperarPeloViewer()
    useAppStore.getState().setSessionRestoreModels(modelosParaReposicao(plano))

    // A triagem partilhada: entra-se nela no banco dos conflitos.
    if (plano.triagem) {
      abrirTriagemDaPartilha({
        id: plano.triagem.peca.id,
        tipo: 'triagem',
        nome: plano.triagem.triagem.codigo,
        recurso: plano.triagem.peca.recurso,
        chave: plano.triagem.peca.chave,
        workspace_nome: s.partilha.workspace_nome,
        dono_nome: s.partilha.dono_nome,
        partilhado_em: new Date().toISOString(),
      })
    }
    // Os BCF: ligados à partilha, sincronizam ao vivo.
    let bcfAtivo: string | null = null
    for (const { bcf, peca } of plano.bcfs) {
      const localId = abrirBcfDaPartilha({
        id: peca.id,
        tipo: 'bcf',
        nome: bcf.nome,
        recurso: peca.recurso,
        chave: bcf.project_id ?? peca.chave,
        workspace_nome: s.partilha.workspace_nome,
        dono_nome: s.partilha.dono_nome,
        partilhado_em: new Date().toISOString(),
      })
      if (peca.id === manifesto.bcf_ativo_partilha_id || bcfAtivo === null) bcfAtivo = localId
    }
    if (bcfAtivo) useAppStore.getState().bcfSetActive(bcfAtivo)
    // Os tópicos já, e não só quando se abrir a lista do BCF (é lá que
    // o ao vivo se liga): sem isto o BCF ficava vazio no viewer, sem
    // pins, até alguém o abrir no módulo.
    for (const { bcf } of plano.bcfs) {
      const entrada = useAppStore.getState().bcfLoaded.find((b) => b.partilhaId === bcf.partilha_id)
      if (!entrada) continue
      definirProgresso(`A trazer os tópicos do BCF "${bcf.nome}"…`)
      try {
        const localId = entrada.localId
        await sincronizar(
          portaDoBcf(
            ctx.workspaceSlug,
            projetoDoBcf(entrada).id,
            localId,
            () => useAppStore.getState().bcfTopicosParaSync(localId),
            (topicos, apagados) => useAppStore.getState().bcfAplicarDaSync(localId, topicos, apagados),
          ),
        )
      } catch (err) {
        avisos.push(`Os tópicos do BCF "${bcf.nome}" não vieram agora: ${mensagem(err)}. Vêm quando o abrir.`)
      }
    }

    gravarSessaoActual(ctx.workspaceId, {
      partilhaId: s.partilha.id,
      chave: null,
      nome,
      minha: false,
    })
    markWorkSaved()
    toast(textoDoResultado(nome, plano), { type: 'success' })
    for (const a of avisos) toast(a, { type: 'info', timeout: 12_000 })
    return true
  } finally {
    definirProgresso(null)
  }
}

/** "Visível para: ..." da sessão, para o menu Sessão. */
export async function visivelParaDaSessao(partilhaId: string): Promise<string | null> {
  try {
    const info = await partilhasApi.ver(partilhaId)
    const inclui = resumoDasPecas(info.pecas ?? [])
    const base = visivelPara(info)
    return inclui ? `${base} Inclui ${inclui}.` : base
  } catch {
    return null
  }
}
